import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Page,
  Layout,
  Card,
  Button,
  Toast,
  BlockStack,
  InlineStack,
  Text,
  Box,
  Spinner,
  Badge,
  Divider,
  DataTable,
  EmptyState,
  Thumbnail,
  Checkbox,
  Pagination,
  Modal,
  Banner,
  TextField,
  Select,  
  ChoiceList,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { useOperationProgressContext } from '../context/OperationProgressContext';
import { useAuthenticatedFetch } from '../hooks/useApi';
import { UpgradeBanner } from '../components/UpgradeBanner';
import { BundleSettings, defaultBundleSettings, ProductBundle, BundlesResponse } from '../types';
import { BundleSettingsForm } from '../components/BundleSettingsForm';
import { BundleModal, BundleFormState } from '../components/BundleModal';

const PAGE_SIZE_OPTIONS = [
  { label: '10', value: '10' },
  { label: '25', value: '25' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
];



// Map column index to sortBy field
const SORT_COLUMN_MAP: Record<number, string> = {
  1: 'name',
  3: 'items',
  4: 'discount',
  5: 'originalPrice',
  6: 'bundlePrice',
  7: 'status',
};

export default function BundlesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shop, shopInfo } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();
  const { startOperation, activeOperations } = useOperationProgressContext();

  // Plan gating: bundles require Growth+
  const hasBundles = shopInfo?.planFeatures?.hasBundles !== false;

  // Settings state
  const [settings, setSettings] = useState<BundleSettings>(defaultBundleSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Bundles list state
  const [bundles, setBundles] = useState<ProductBundle[]>([]);
  const [isLoadingBundles, setIsLoadingBundles] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Selection state
  const [selectedBundles, setSelectedBundles] = useState<Set<string>>(new Set());

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState<ProductBundle | null>(null);
  const [isSavingBundle, setIsSavingBundle] = useState(false);

  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Toggle active status state
  const [togglingBundles, setTogglingBundles] = useState<Set<string>>(new Set());

  // Check if bundle generation is in progress (from SignalR)
  const bundleOperation = activeOperations.find(op => 
    op.operationType === 'RefreshBundles' && 
    (op.status === 'started' || op.status === 'progress')
  );
  const generationInProgress = !!bundleOperation;

  // Sorting state (server-side)
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortColumnIndex, setSortColumnIndex] = useState<number | undefined>(undefined);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInputValue, setSearchInputValue] = useState('');

  // Toast state
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'settings' | 'bundles'>('settings');

  // Load settings
  const loadSettings = useCallback(async () => {
    if (!shop) return;

    setIsLoading(true);
    try {
      const data = await fetchWithAuth<BundleSettings>('/api/bundles/settings');
      setSettings({ ...defaultBundleSettings, ...data });
    } catch (error) {
      console.error('Error loading bundle settings:', error);
      showToast(t('bundles.errorLoadingSettings'), true);
    } finally {
      setIsLoading(false);
    }
  }, [shop, fetchWithAuth]);

  // Load bundles with server-side sorting, filtering and pagination
  const loadBundles = useCallback(async (
    page = 1, 
    limit = pageSize,
    search = searchQuery,
    status = statusFilter,
    sort = sortBy,
    order = sortOrder
  ) => {
    if (!shop) return;

    setIsLoadingBundles(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        page: page.toString(),
        includeInactive: 'true',
        statusFilter: status,
        sortOrder: order,
      });
      
      if (search) params.append('searchQuery', search);
      if (sort) params.append('sortBy', sort);

      const data = await fetchWithAuth<BundlesResponse>(`/api/bundles?${params.toString()}`);
      setBundles(data.bundles || []);
      setTotalCount(data.totalCount || 0);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(data.page || 1);
      setSelectedBundles(new Set()); // Clear selection on page change
    } catch (error) {
      console.error('Error loading bundles:', error);
    } finally {
      setIsLoadingBundles(false);
    }
  }, [shop, fetchWithAuth, pageSize, searchQuery, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    loadSettings();
    loadBundles(1);
  }, [loadSettings]); // loadBundles not in deps to avoid infinite loop

  // Save settings
  const saveSettings = useCallback(async () => {
    if (!shop) return;

    setIsSaving(true);
    try {
      await fetchWithAuth('/api/bundles/settings', {
        method: 'POST',
        body: settings,
      });
      setHasChanges(false);
      showToast(t('settings.saved'));
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast(error instanceof Error ? error.message : t('common.error'), true);
    } finally {
      setIsSaving(false);
    }
  }, [shop, settings, fetchWithAuth, t]);

  // Generate bundles via Azure Functions with SignalR progress
  const generateBundles = useCallback(async () => {
    if (!shop || isGenerating || generationInProgress) return;

    setIsGenerating(true);

    try {
      const response = await fetchWithAuth<{ correlationId: string; message: string }>(
        '/api/bundles/refresh-async',
        { method: 'POST' }
      );

      console.log('[BundlesPage] refresh-async succeeded, correlationId:', response.correlationId);
      
      // Start tracking the operation in the global progress context
      startOperation(response.correlationId, 'RefreshBundles', t('bundles.generationStarting', 'Starting bundle generation...'));
    } catch (error) {
      console.error('[BundlesPage] refresh-async failed:', error);
      showToast(error instanceof Error ? error.message : t('bundles.generateError'), true);
    } finally {
      setIsGenerating(false);
    }
  }, [shop, isGenerating, generationInProgress, fetchWithAuth, startOperation, t]);

  // Reload bundles when bundle operation completes
  useEffect(() => {
    const completedBundleOp = activeOperations.find(op => 
      op.operationType === 'RefreshBundles' && 
      op.status === 'completed'
    );
    
    if (completedBundleOp) {
      // Reload bundles after a brief delay
      setTimeout(() => loadBundles(1), 500);
    }
  }, [activeOperations, loadBundles]);

  // Open modal for creating new bundle
  const handleCreateBundle = useCallback(() => {
    setEditingBundle(null);
    setModalOpen(true);
  }, []);

  // Open modal for editing
  const handleEditBundle = useCallback((bundle: ProductBundle) => {
    setEditingBundle(bundle);
    setModalOpen(true);
  }, []);

  // Save bundle (create or update)
  const handleSaveBundle = useCallback(async (formState: BundleFormState) => {
    if (!shop) return;

    setIsSavingBundle(true);
    try {
      if (formState.id) {
        // Update existing
        await fetchWithAuth(`/api/bundles/${formState.id}`, {
          method: 'PUT',
          body: {
            shopDomain: shop,
            name: formState.name,
            description: formState.description,
            discountType: formState.discountType,
            discountValue: formState.discountValue,
            bundleType: formState.bundleType,
            isActive: formState.isActive,
            products: formState.products.map(p => ({
              productId: p.productId,
              variantId: p.variantId,
              quantity: p.quantity,
              isAnchor: p.isAnchor,
            })),
          },
        });
        showToast(t('bundles.bundleUpdated'));
      } else {
        // Create new bundle
        if (formState.products.length < 2) {
          showToast(t('bundles.minProducts'), true);
          setIsSavingBundle(false);
          return;
        }
        
        await fetchWithAuth('/api/bundles', {
          method: 'POST',
          body: {
            shopDomain: shop,
            name: formState.name,
            description: formState.description,
            discountType: formState.discountType,
            discountValue: formState.discountValue,
            bundleType: formState.bundleType,
            products: formState.products.map(p => ({
              productId: p.productId,
              variantId: p.variantId,
              quantity: p.quantity,
              isAnchor: p.isAnchor,
            })),
          },
        });
        showToast(t('bundles.bundleCreated'));
      }
      setModalOpen(false);
      await loadBundles(currentPage);
    } catch (error) {
      console.error('Error saving bundle:', error);
      showToast(error instanceof Error ? error.message : t('bundles.errorSaving'), true);
    } finally {
      setIsSavingBundle(false);
    }
  }, [shop, fetchWithAuth, loadBundles, currentPage]);

  // Delete selected bundles
  const handleDeleteSelected = useCallback(async () => {
    if (!shop || selectedBundles.size === 0) return;

    setIsDeleting(true);
    try {
      const bundleIds = Array.from(selectedBundles);
      
      if (bundleIds.length === 1) {
        // Single delete
        await fetchWithAuth(`/api/bundles/${bundleIds[0]}`, {
          method: 'DELETE',
        });
      } else {
        // Batch delete
        await fetchWithAuth('/api/bundles/delete-many', {
          method: 'POST',
          body: { bundleIds },
        });
      }

      showToast(t('bundles.deletedCount', { count: bundleIds.length }));
      setDeleteModalOpen(false);
      setSelectedBundles(new Set());
      await loadBundles(currentPage);
    } catch (error) {
      console.error('Error deleting bundles:', error);
      showToast(error instanceof Error ? error.message : t('bundles.errorDeletingBundles'), true);
    } finally {
      setIsDeleting(false);
    }
  }, [shop, selectedBundles, fetchWithAuth, loadBundles, currentPage]);

  // Toggle bundle active status
  const handleToggleActive = useCallback(async (bundle: ProductBundle) => {
    if (!shop) return;

    setTogglingBundles(prev => new Set([...prev, bundle.id]));
    try {
      await fetchWithAuth(`/api/bundles/${bundle.id}`, {
        method: 'PUT',
        body: {
          shopDomain: shop,
          isActive: !bundle.isActive,
        },
      });
      showToast(t('bundles.statusUpdated'));
      // Update local state to reflect the change immediately
      setBundles(prev => prev.map(b => 
        b.id === bundle.id ? { ...b, isActive: !b.isActive } : b
      ));
    } catch (error) {
      console.error('Error toggling bundle status:', error);
      showToast(t('bundles.statusUpdateError'), true);
    } finally {
      setTogglingBundles(prev => {
        const newSet = new Set(prev);
        newSet.delete(bundle.id);
        return newSet;
      });
    }
  }, [shop, fetchWithAuth, t]);

  // Toggle bundle selection
  const toggleBundleSelection = useCallback((bundleId: string) => {
    setSelectedBundles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bundleId)) {
        newSet.delete(bundleId);
      } else {
        newSet.add(bundleId);
      }
      return newSet;
    });
  }, []);

  // Toggle select all on current page
  const toggleSelectAll = useCallback(() => {
    if (selectedBundles.size === bundles.length) {
      setSelectedBundles(new Set());
    } else {
      setSelectedBundles(new Set(bundles.map(b => b.id)));
    }
  }, [bundles, selectedBundles.size]);

  // Pagination handlers
  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      loadBundles(currentPage + 1, pageSize, searchQuery, statusFilter, sortBy, sortOrder);
    }
  }, [currentPage, totalPages, loadBundles, pageSize, searchQuery, statusFilter, sortBy, sortOrder]);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      loadBundles(currentPage - 1, pageSize, searchQuery, statusFilter, sortBy, sortOrder);
    }
  }, [currentPage, loadBundles, pageSize, searchQuery, statusFilter, sortBy, sortOrder]);

  // Update settings
  const handleSettingsChange = useCallback((newSettings: BundleSettings) => {
    setSettings(newSettings);
    setHasChanges(true);
  }, []);

  // Toast helper
  const showToast = (message: string, error = false) => {
    setToastMessage(message);
    setToastError(error);
    setToastActive(true);
  };

  // Format currency based on shop's currency
  const formatCurrency = (value: number) => {
    const currency = shopInfo?.currency || 'EUR';
    // Map currency to appropriate locale
    const localeMap: Record<string, string> = {
      'EUR': 'es-ES',
      'USD': 'en-US',
      'GBP': 'en-GB',
      'JPY': 'ja-JP',
      'CAD': 'en-CA',
      'AUD': 'en-AU',
      'CHF': 'de-CH',
      'CNY': 'zh-CN',
      'MXN': 'es-MX',
      'BRL': 'pt-BR',
    };
    const locale = localeMap[currency] || 'es-ES';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  // Handle sort (server-side)
  const handleSort = useCallback((headingIndex: number, direction: 'ascending' | 'descending') => {
    const newSortBy = SORT_COLUMN_MAP[headingIndex];
    const newSortOrder = direction === 'ascending' ? 'asc' : 'desc';
    
    setSortColumnIndex(headingIndex);
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    
    // Reload with new sort
    loadBundles(1, pageSize, searchQuery, statusFilter, newSortBy, newSortOrder);
  }, [loadBundles, pageSize, searchQuery, statusFilter]);

  // Handle search
  const handleSearch = useCallback(() => {
    setSearchQuery(searchInputValue);
    setCurrentPage(1);
    loadBundles(1, pageSize, searchInputValue, statusFilter, sortBy, sortOrder);
  }, [loadBundles, pageSize, searchInputValue, statusFilter, sortBy, sortOrder]);

  // Handle search clear
  const handleSearchClear = useCallback(() => {
    setSearchInputValue('');
    setSearchQuery('');
    setCurrentPage(1);
    loadBundles(1, pageSize, '', statusFilter, sortBy, sortOrder);
  }, [loadBundles, pageSize, statusFilter, sortBy, sortOrder]);

  // Handle status filter change
  const handleStatusFilterChange = useCallback((value: string[]) => {
    const newStatus = value[0] || 'all';
    setStatusFilter(newStatus);
    setCurrentPage(1);
    loadBundles(1, pageSize, searchQuery, newStatus, sortBy, sortOrder);
  }, [loadBundles, pageSize, searchQuery, sortBy, sortOrder]);

  // Handle page size change
  const handlePageSizeChange = useCallback((value: string) => {
    const newSize = parseInt(value, 10);
    setPageSize(newSize);
    setCurrentPage(1);
    loadBundles(1, newSize, searchQuery, statusFilter, sortBy, sortOrder);
  }, [loadBundles, searchQuery, statusFilter, sortBy, sortOrder]);

  // Check if any filters are applied
  const hasFilters = searchQuery || statusFilter !== 'all';

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSearchInputValue('');
    setSearchQuery('');
    setStatusFilter('all');
    setCurrentPage(1);
    loadBundles(1, pageSize, '', 'all', sortBy, sortOrder);
  }, [loadBundles, pageSize, sortBy, sortOrder]);

  // Render bundles table
  const renderBundlesTable = () => {
    if (isLoadingBundles) {
      return (
        <Box padding="800">
          <InlineStack align="center">
            <Spinner size="large" />
          </InlineStack>
        </Box>
      );
    }

    if (bundles.length === 0) {
      return (
        <EmptyState
          heading={hasFilters ? t('bundles.noResults') : t('bundles.noBundle')}
          action={hasFilters ? {
            content: t('bundles.clearFilters'),
            onAction: handleClearFilters,
          } : {
            content: t('bundles.generateBundles'),
            onAction: generateBundles,
            loading: isGenerating,
          }}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <p>
            {hasFilters ? t('bundles.noResultsDesc') : t('bundles.noBundleDesc')}
          </p>
        </EmptyState>
      );
    }

    const allSelected = selectedBundles.size === bundles.length && bundles.length > 0;

    const rows = bundles.map((bundle) => [
      <Checkbox
        label=""
        labelHidden
        checked={selectedBundles.has(bundle.id)}
        onChange={() => toggleBundleSelection(bundle.id)}
      />,
      <Button variant="plain" onClick={() => handleEditBundle(bundle)}>
        {bundle.name}
      </Button>,
      <InlineStack gap="100" wrap={false}>
        {bundle.products.slice(0, 3).map((p, i) => (
          <Thumbnail
            key={i}
            source={p.imageUrl || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'}
            alt={p.title}
            size="small"
          />
        ))}
        {bundle.products.length > 3 && (
          <Badge>{`+${bundle.products.length - 3}`}</Badge>
        )}
      </InlineStack>,
      `${bundle.products.length} ${t('bundles.products')}`,
      <BlockStack gap="100">
        <Text as="span" variant="bodyMd">
          {bundle.discountType === 'percentage' 
            ? `${bundle.discountValue}% ${t('bundles.off')}`
            : formatCurrency(bundle.discountValue)}
        </Text>
        {bundle.shopifyDiscountCode && (
          <Text as="span" variant="bodySm" tone="subdued">
            <code style={{ fontFamily: 'monospace', fontSize: '11px' }}>{bundle.shopifyDiscountCode}</code>
          </Text>
        )}
      </BlockStack>,
      formatCurrency(bundle.originalPrice),
      <Text as="span" fontWeight="bold" tone="success">
        {formatCurrency(bundle.bundlePrice)}
      </Text>,
      <span style={{ cursor: 'pointer' }} onClick={() => !togglingBundles.has(bundle.id) && handleToggleActive(bundle)}>
        {togglingBundles.has(bundle.id) ? (
          <Spinner size="small" />
        ) : (
          <Badge tone={bundle.isActive ? 'success' : 'attention'}>
            {bundle.isActive ? t('bundles.active') : t('bundles.inactive')}
          </Badge>
        )}
      </span>,
      <Button variant="plain" onClick={() => handleEditBundle(bundle)}>
        {t('common.edit')}
      </Button>,
    ]);

    return (
      <BlockStack gap="400">
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'numeric', 'text', 'text']}
          headings={[
            <Checkbox
              label=""
              labelHidden
              checked={allSelected}
              onChange={toggleSelectAll}
            />,
            t('bundles.table.name'),
            t('bundles.table.products'),
            t('bundles.table.items'),
            t('bundles.table.discount'),
            t('bundles.table.original'),
            t('bundles.table.bundlePrice'),
            t('bundles.table.status'),
            t('bundles.table.actions'),
          ]}
          rows={rows}
          sortable={[false, true, false, true, true, true, true, true, false]}
          defaultSortDirection="ascending"
          initialSortColumnIndex={sortColumnIndex}
          onSort={handleSort}
        />
        
        {/* Pagination and page size */}
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="span" variant="bodySm" tone="subdued">
              {t('bundles.table.showing', { from: ((currentPage - 1) * pageSize) + 1, to: Math.min(currentPage * pageSize, totalCount), total: totalCount })}
            </Text>
            <Select
              label=""
              labelHidden
              options={PAGE_SIZE_OPTIONS}
              value={pageSize.toString()}
              onChange={handlePageSizeChange}
            />
            <Text as="span" variant="bodySm" tone="subdued">
              {t('bundles.perPage')}
            </Text>
          </InlineStack>
          {totalPages > 1 && (
            <Pagination
              hasPrevious={currentPage > 1}
              hasNext={currentPage < totalPages}
              onPrevious={handlePreviousPage}
              onNext={handleNextPage}
            />
          )}
        </InlineStack>
      </BlockStack>
    );
  };

  if (isLoading) {
    return (
      <Page title={t('bundles.title')}>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center">
                  <Spinner size="large" />
                  <Text as="p">{t('common.loading')}</Text>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!hasBundles) {
    return (
      <Page
        title={t('bundles.title')}
        backAction={{ content: t('common.back'), onAction: () => navigate('/app') }}
      >
        <Layout>
          <Layout.Section>
            <UpgradeBanner feature="bundles" requiredPlan="Growth" />
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t('bundles.title')}
      subtitle={t('bundles.subtitle')}
      backAction={{ content: t('common.back'), onAction: () => navigate('/app') }}
      primaryAction={{
        content: t('bundles.saveChanges'),
        onAction: saveSettings,
        loading: isSaving,
        disabled: !hasChanges,
      }}
    >
        <Layout>
          {/* Stats Overview */}
          <Layout.Section>
            <Card>
              <InlineStack gap="800" align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingLg">{totalCount}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{t('bundles.totalBundles')}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="h2" variant="headingLg">{settings.defaultDiscountPercentage}%</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{t('bundles.defaultDiscount')}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="h2" variant="headingLg">{settings.maxProductsPerBundle}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{t('bundles.maxProducts')}</Text>
                </BlockStack>
                <Badge tone={settings.autoGenerateEnabled ? 'success' : 'attention'} size="large">
                  {settings.autoGenerateEnabled ? t('bundles.autoGenerateOn') : t('bundles.autoGenerateOff')}
                </Badge>
              </InlineStack>
            </Card>
          </Layout.Section>

          {/* Tab buttons */}
          <Layout.Section>
            <InlineStack gap="200">
              <Button
                variant={activeTab === 'settings' ? 'primary' : 'secondary'}
                onClick={() => setActiveTab('settings')}
              >
                {t('bundles.settings')}
              </Button>
              <Button
                variant={activeTab === 'bundles' ? 'primary' : 'secondary'}
                onClick={() => setActiveTab('bundles')}
              >
                {`${t('bundles.bundlesList')} (${totalCount})`}
              </Button>
            </InlineStack>
          </Layout.Section>

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <Layout.Section>
              <BundleSettingsForm
                settings={settings}
                onChange={handleSettingsChange}
              />
            </Layout.Section>
          )}

          {/* Bundles Tab */}
          {activeTab === 'bundles' && (
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <InlineStack gap="300" blockAlign="center">
                      <Text as="h2" variant="headingMd">{t('bundles.yourBundles')}</Text>
                      {selectedBundles.size > 0 && (
                        <Badge tone="info">{`${selectedBundles.size} ${t('bundles.selected')}`}</Badge>
                      )}
                    </InlineStack>
                    <InlineStack gap="200">
                      {selectedBundles.size > 0 && (
                        <Button
                          onClick={() => setDeleteModalOpen(true)}
                          variant="primary"
                          tone="critical"
                        >
                          {`${t('bundles.deleteSelected')} (${selectedBundles.size})`}
                        </Button>
                      )}
                      <Button
                        onClick={handleCreateBundle}
                        variant="secondary"
                      >
                        {t('bundles.createBundle')}
                      </Button>
                      <Button
                        onClick={generateBundles}
                        loading={isGenerating || generationInProgress}
                        disabled={generationInProgress}
                        variant="primary"
                      >
                        {generationInProgress 
                          ? bundleOperation?.message || t('bundles.generating', 'Generating...') 
                          : t('bundles.regenerateAll')}
                      </Button>
                    </InlineStack>
                  </InlineStack>

                  <Divider />
                  
                  {/* Search and Filters */}
                  <InlineStack gap="300" blockAlign="end">
                    <div style={{ flex: 1, maxWidth: '300px' }}>
                      <TextField
                        label=""
                        labelHidden
                        placeholder={t('bundles.searchPlaceholder')}
                        value={searchInputValue}
                        onChange={setSearchInputValue}
                        onBlur={handleSearch}
                        clearButton
                        onClearButtonClick={handleSearchClear}
                        autoComplete="off"
                        connectedRight={
                          <Button onClick={handleSearch}>{t('common.search')}</Button>
                        }
                      />
                    </div>
                    <ChoiceList
                      title=""
                      titleHidden
                      choices={[
                        { label: t('bundles.filterAll'), value: 'all' },
                        { label: t('bundles.filterActive'), value: 'active' },
                        { label: t('bundles.filterInactive'), value: 'inactive' },
                      ]}
                      selected={[statusFilter]}
                      onChange={handleStatusFilterChange}
                    />
                    {hasFilters && (
                      <Button variant="plain" onClick={handleClearFilters}>
                        {t('bundles.clearFilters')}
                      </Button>
                    )}
                  </InlineStack>
                  
                  {renderBundlesTable()}
                </BlockStack>
              </Card>
            </Layout.Section>
          )}

          {/* Help Section */}
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">{t('bundles.howToDisplay')}</Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t('bundles.howToDisplayDesc')}
                </Text>
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">
                    {t('bundles.step1')}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    {t('bundles.step2')}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    {t('bundles.step3')}
                  </Text>
                </BlockStack>
                <Box paddingBlockStart="200">
                  <Button
                    url={shop ? `https://${shop}/admin/themes/current/editor` : '#'}
                    external
                  >
                    {t('bundles.openThemeEditor')}
                  </Button>
                </Box>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>

        {toastActive && (
          <Toast
            content={toastMessage}
            error={toastError}
            onDismiss={() => setToastActive(false)}
          />
        )}

        {/* Bundle Edit Modal */}
        <BundleModal
          open={modalOpen}
          bundle={editingBundle}
          onClose={() => {
            setModalOpen(false);
            setEditingBundle(null);
          }}
          onSave={handleSaveBundle}
          isLoading={isSavingBundle}
          currency={shopInfo?.currency || 'EUR'}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title={t('bundles.deleteConfirmTitle')}
          primaryAction={{
            content: t('bundles.deleteButton', { count: selectedBundles.size }),
            onAction: handleDeleteSelected,
            loading: isDeleting,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: t('common.cancel'),
              onAction: () => setDeleteModalOpen(false),
            },
          ]}
        >
          <Modal.Section>
            <Banner tone="warning">
              <p>
                {t('bundles.deleteConfirmMessage', { count: selectedBundles.size })}
              </p>
            </Banner>
        </Modal.Section>
        </Modal>
    </Page>
  );
}
